import {Paper, Grid, Button, FormControl, MenuItem, Select, Typography, IconButton} from "@material-ui/core";
import {lighten} from '@material-ui/core/styles';
import {useState, useEffect} from "react";
import {toBase64} from '../utils/toBase64';
import VisibilityIcon from '@material-ui/icons/Visibility';
import {makeStyles} from "@material-ui/core/styles";
import StepNavigator from "../components/StepNavigator";
import {createImage, deleteImage, updateImage} from '../api/image'
import {uploadLicense, removeLicense} from '../api/provider'
import UploadImagesDialog from "../components/UploadImagesDialog";
import AdditionalNotes from "../components/AdditionalNotes";
import PhotoLibraryIcon from '@material-ui/icons/PhotoLibraryOutlined';
import UploadIconBlack from '../assets/icons/upload_black_32.png'
import {useAlert} from "react-alert";
import ClearIcon from '@material-ui/icons/Clear';
import ImageSetEditor from "../components/ImageSetEditor";

const useStyles = makeStyles((theme) => ({
    container: {
        position: 'relative',
        height: '100%',
        padding: theme.spacing(2),
        marginTop: theme.spacing(2),
        borderRadius: theme.shape.borderRadius,
        flex: '1',
        display: 'flex',
        boxShadow: "0px 4px 4px rgba(0, 0, 0, 0.25)",
        background: '#2f3135'
    },
    narrowGrid: {
        '& > .MuiGrid-grid-xs-2': {
            flexGrow: 0,
            maxWidth: '14%',
            flexBasis: '14%',
        },
        '& > .MuiGrid-grid-xs-10': {
            flexGrow: 0,
            maxWidth: '86%',
            flexBasis: '86%',
            '@media (max-width:1366px)': {
                flex: '1',
            },
        }
    },
    pageNavigation: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-start',
        borderRadius: theme.shape.borderRadius,
        background: '#32373D',
        boxShadow: '0px 4px 4px #000000',
        minWidth: 180,
        padding: '0 !important',
        overflow: 'hidden',
        '& .MuiTypography-h4': {
            marginTop: theme.spacing(2),
            marginBottom: theme.spacing(4),
            '@media (max-width:1440px)': {
                fontSize: '1.5rem',
                marginTop: theme.spacing(1),
                marginBottom: theme.spacing(2),
            },
        },
        '& ul': {
            '@media (max-width:1440px)': {
                paddingLeft: theme.spacing(2),
                fontSize: '0.75rem',
            },
        },
        '& .MuiButton-label': {
            '@media ( max-width: 1440px)': {
                fontSize: '0.70rem',
                '& > .MuiButton-startIcon img': {
                    height: 12,
                }
            }
        }
    },
    pageContent: {
        padding: `16px !important`,
        borderRadius: theme.shape.borderRadius,
        height: '100%',
        overflowY: 'auto',
    },
    content: {
        flex: "1",
        padding: theme.spacing(2),
        textAlign: 'center',
        width: '100%',
    },
    yellowButton: {
        color: theme.palette.getContrastText(theme.customColors.yellow),
        backgroundColor: theme.customColors.yellow,
        '&:hover': {
            backgroundColor: lighten(theme.customColors.yellow, '0.2'),
        },
    },
    badge: {
        '.MuiInputBase-input &': {
            display: 'none',
        },
        display: 'inline-block',
        width: 8,
        height: 8,
        fontSize: 10,
        color: '#000',
        textAlign: 'center',
        borderRadius: '100%',
        marginRight: 8,
        background: theme.customColors.yellow,
    }
}));

const openInNewTab = (url) => {
    const newWindow = window.open(url, '_blank', 'noopener,noreferrer')
    if (newWindow) newWindow.opener = null
}

const SelectImageMessage = () => {
    return (
        <div style={{height: '100%', width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column', textAlign: 'center'}}>
            <PhotoLibraryIcon style={{fontSize: '8rem', marginBottom: 16}} />
            <Typography>
                Please click on the <b>Upload Pictures</b> button to start adding the pictures<br /> of your branch and on the <b>Upload Trade License</b> button to upload your trade license.
            </Typography>
        </div>

    )
}


export default function BranchesPage({data, token, onUpdate, onNext, pageParams, onPrev, onLoading}) {
    const classes = useStyles();
    const alert = useAlert();

    const [updateTime, setUpdateTime] = useState(+Date.now());
    const [uploadImageDialogActive, setUploadImageDialogActive] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const branches = data.branches
    const [selectedBranch, setSelectedBranch] = useState(-1);


    useEffect(() => {

        if (pageParams.branch) {
            const branchIndex = data.branches.findIndex(b => b._id === pageParams.branch)
            setSelectedBranch(branchIndex);
            return;
        }

        if (data.branches.length === 1 && data.tokenType === 'BRANCH') {
            setSelectedBranch(0)
        }
    }, [data.branches, data.tokenType, pageParams])

    const handleImageDelete = (index) => {
        const imageId = data.branches[selectedBranch].images[index]._id;

        deleteImage(imageId, token).then(_ => {
            data.branches[selectedBranch].images.splice(index, 1);
            onUpdate({...data, branches: data.branches})
            setUpdateTime(+Date.now());
        }).catch(() => {
            alert.show("Failed to delete image")
        })
    }

    const handleImageUpdate = (index, image) => {
        console.log("updaating index", index)
        // todo: do update image here
        onLoading(true);
        updateImage({
            imageId: image._id,
            editedUri: image.editedUri,
            editData: image.editData
        }, token).then(res => {
            res.editedUri = res.editedUri + `?ts=${+Date.now()}`
            data.branches[selectedBranch].images[index] = res;
            onUpdate({...data, branches: data.branches, })
            setUpdateTime(+Date.now());
        }).catch(() => alert.show("Failed to update image"))
            .then(() => {onLoading(false);})
    }

    const handleImageAdd = (image) => {
        console.log("adding image")
        data.branches[selectedBranch].images.push(image);
        onUpdate({...data, branches: data.branches})
        setUpdateTime(+Date.now());
    }

    const handleUpload = async (files) => {
        console.log(files);
        onLoading(true);
        const promises = files.map(async file => {
            try {
                const imageUri = await toBase64(file)
                const res = await createImage({
                    sourceId: branches[selectedBranch]._id,
                    fileName: `${+Date.now()}.png`,
                    imageType: 'BRANCH_IMAGE',
                    originalUri: imageUri
                }, token);
                handleImageAdd(res);
            } catch (e) {
                alert.show("Failed to uplaod iamge")
                console.error(e);
            }
        });
        setUploadImageDialogActive(false);
        await Promise.all(promises);
        onLoading(false);
    }

    const handleLicenseUpload = async (e) => {
        console.log("Upload", e.target.files[0])
        try {
            const file = e.target.files[0];
            onLoading(true);
            const fileUri = await toBase64(file)
            const res = await uploadLicense({
                branchId: branches[selectedBranch]._id,
                tradeLicenseFile: fileUri,
            }, token)
            data.branches[selectedBranch].tradeLicense = res.url;
            onUpdate({...data, branches: data.branches})
        } catch (e) {
            alert.show("failed to upload licenese")
        } finally {
            onLoading(false);
        }
    }

    const handleLicenseDelete = async (index) => {
        try {
            onLoading(true);
            const res = await removeLicense(branches[index]._id, token)
            data.branches[index].tradeLicense = res.url;
            onUpdate({...data, branches: data.branches})
            console.log(res)
        } catch {
            alert.show("failed to delete license")
        } finally {
            onLoading(false);
        }

    }


    const handleNoteChange = (value) => {
        const note = {
            sourceId: data.branches[selectedBranch]._id,
            type: 'BRANCH_NOTE',
            content: value,
        }
        data.branches[selectedBranch].note = note;
        onUpdate({
            ...data,
            branches: data.branches
        })
    }


    return (
        <>
            <Paper className={classes.container}>
                <Grid container spacing={1} className={classes.narrowGrid}>
                    <Grid item xs={2} className={classes.pageNavigation}>
                        <div className={classes.content}>
                            <Typography variant="h4" gutterBottom>Branches</Typography>
                            <FormControl fullWidth style={{width: '100%'}}>
                                {/*<InputLabel id="demo-simple-select-label">Branch</InputLabel>*/}
                                <Select
                                    labelId="demo-simple-select-label"
                                    id="demo-simple-select"
                                    style={{marginBottom: 16}}
                                    value={selectedBranch}
                                    disabled={data.tokenType === 'BRANCH'}
                                    onChange={(e) => setSelectedBranch(e.target.value)}
                                >
                                    <MenuItem value={-1} disabled>Select Branch</MenuItem>
                                    {
                                        branches.map((branch, index) => (
                                            <MenuItem value={index} key={branch._id}>
                                                {branch?.images?.length === 0 && <div className={classes.badge}></div>} {branch?.info?.name}
                                            </MenuItem>
                                        ))
                                    }
                                </Select>
                            </FormControl>
                            <Button
                                variant="contained"
                                onClick={() => setUploadImageDialogActive(true)}
                                color="primary"
                                disabled={selectedBranch === -1}
                                startIcon={<img src={UploadIconBlack} alt="upload" height="18" />}>
                                Upload Pictures
                            </Button>
                            <ul style={{textAlign: 'left', }}>
                                <li>
                                    The size of each picture should be no less than 500 Kb and no greater than 5 Mb.
                                </li>
                                <li>
                                    The picture type should be .jpg or .jpeg.
                                </li>
                            </ul>
                        </div>
                        <div style={{width: '100%', padding: 16, background: 'rgba(158, 158, 158, 0.1)', fontWeight: 'bold', textAlign: 'center'}}>
                           <Typography style={{fontWeight: 'bold'}}>Trade License</Typography> 
                        </div>
                        {
                            (branches[selectedBranch]?.tradeLicense == null) ? (
                                <div style={{padding: `16px 8px`}}>
                                    <input
                                        accept="image/*,application/pdf"
                                        style={{display: 'none'}}
                                        onChange={handleLicenseUpload}
                                        id="contained-button-file"
                                        type="file"
                                    />
                                    <label htmlFor="contained-button-file">
                                        <Button
                                            variant="contained"
                                            color="secondary"
                                            disabled={selectedBranch === -1}
                                            className={classes.yellowButton}
                                            component="span"
                                            startIcon={<img src={UploadIconBlack} alt="upload" height="18" />}>
                                            Upload Trade License
                                        </Button>
                                    </label>
                                </div>
                            ) : (
                                <div style={{width: '100%', padding: 16, display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                                    <span style={{flex: '1'}}>Trade License</span>
                                    <IconButton onClick={() => openInNewTab(data.branches[selectedBranch].tradeLicense)}><VisibilityIcon /></IconButton>
                                    <IconButton onClick={() => handleLicenseDelete(selectedBranch)}><ClearIcon /></IconButton>
                                </div>
                            )
                        }
                        <AdditionalNotes
                            disabled={selectedBranch === -1}
                            value={data.branches[selectedBranch]?.note?.content}
                            onChange={handleNoteChange} />
                    </Grid>
                    <Grid item xs={10} className={classes.pageContent}>
                        {
                            (selectedBranch === -1 || branches[selectedBranch].images.length === 0) ? (
                                <SelectImageMessage />
                            ) : (
                                <>
                                    <Typography variant="h5">{branches[selectedBranch].name}</Typography>
                                    <ImageSetEditor
                                        images={branches[selectedBranch].images}
                                        onEditing={setIsEditing}
                                        updateTime={updateTime}
                                        handleUpdate={handleImageUpdate}
                                        handleDelete={handleImageDelete}
                                    />

                                </>
                            )
                        }
                    </Grid>
                </Grid>
            </Paper>
            <UploadImagesDialog isOpen={uploadImageDialogActive} onClose={() => setUploadImageDialogActive(false)} onUpload={handleUpload} />
            <StepNavigator disabled={isEditing} onNext={onNext} onPrev={onPrev} />
        </>
    );
}
